import { FC, MouseEvent, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Typography } from '@mui/material';
import { useUserLimits } from 'store/useUserLimits';
import { getUserLimits } from 'store/useUserLimits/selectors';
import { useUserSubscriptionStore } from 'store/useUserSubscriptionStore';
import { getUserSubscription } from 'store/useUserSubscriptionStore/selectors';

import { PdfDownloadButton } from 'pages/BusinessToolPage/BusinessToolHeader/PdfDownloadButton';
import { checkIsOnePageBP } from 'pages/BusinessToolPage/utils';
import useBusinessPlanActionMenuHandlers from 'pages/Workspace/hooks/useBusinessPlanActionMenuHandlers';

import { ReactComponent as ArrowPathIcon } from 'assets/icons/ArrowPathIcon.svg';
import { ReactComponent as ArrowRightUpIcon } from 'assets/icons/ArrowRightUpIcon.svg';
import { ReactComponent as ChainsIcon } from 'assets/icons/ChainsIcon.svg';
import { ReactComponent as CopyIcon } from 'assets/icons/CopyIcon.svg';
import { ReactComponent as DownloadIcon } from 'assets/icons/DownloadIcon.svg';
import { ReactComponent as EditIcon } from 'assets/icons/EditIcon.svg';
import { ReactComponent as PaperAirplaneIcon } from 'assets/icons/PaperAirplaneIcon.svg';
import { ReactComponent as RenameIcon } from 'assets/icons/RenameIcon.svg';
import { ReactComponent as ShareIcon } from 'assets/icons/ShareIcon.svg';
import { ReactComponent as TrashBinIcon } from 'assets/icons/TrashBinIcon.svg';

import usePromiseStatus from 'hooks/usePromiseStatus';
import { useSharePDF } from 'hooks/useSharePDF';

import { IBusinessPlanEntity, ILimitEntity } from 'interfaces/businessPlan';
import { SUBSCRIPTION_PLAN } from 'interfaces/subscriptions';

import { getTimeAgoString } from 'helpers/formatDates';
import { getDocumentType } from 'helpers/getDocumentType';
import { checkIsHighestSubscription } from 'helpers/subscriptions';

import { CardTitleComponent } from './CardTitleComponent/CardTitleComponent';
import { BusinessPlanActionsMenu } from '../BusinessPlanCardActionsMenu/BusinessPlanCardActionsMenu';
import { ASSISTANT, COPY, COPY_PDF_LINK, CREATE_PDF, DELETE, EDIT, PDF, RENAME, SHARE, UPDATE_PDF } from '../constants';
import { getCardActionsList } from '../helper';
import { ICardActionProperties } from '../types';

import styles from './CardLowerSection.module.scss';

export const CardLowerSection: FC<IBusinessPlanEntity> = ({
  title,
  updatedAt,
  viewonly,
  type,
  uuid,
  metadata,
  ...rest
}) => {
  const handlers = useBusinessPlanActionMenuHandlers({ title, updatedAt, viewonly, type, uuid, metadata, ...rest });

  const { t, i18n } = useTranslation();
  const { track } = usePromiseStatus();

  const [showInputField, setShowInputField] = useState(false);

  const generatePDFRef = useRef<HTMLButtonElement>(null);
  const uploadPDFRef = useRef<HTMLButtonElement>(null);

  const limits = useUserLimits(getUserLimits);
  const availabilityCount = limits.availableDocumentCount[getDocumentType(type) as keyof ILimitEntity];

  const userSubscription = useUserSubscriptionStore(getUserSubscription);

  const isHighestSubscription = checkIsHighestSubscription(userSubscription?.currentSubscription.subscriptionPlan);
  const isOnePageBP = checkIsOnePageBP(type);

  const isStandardOrLightTariff =
    userSubscription?.currentSubscription?.subscriptionPlan === SUBSCRIPTION_PLAN.STANDARD ||
    userSubscription?.currentSubscription?.subscriptionPlan === SUBSCRIPTION_PLAN.LIGHT;
  const parsedMetadata = typeof metadata === 'string' ? JSON.parse(metadata) : metadata;
  const isUpdateOnlinePDFAvailable = parsedMetadata.updateOnlinePDFCount < 2 || isStandardOrLightTariff;
  const isGeneratePDFAvailable = parsedMetadata.downloadDocumentCount < 1 || isStandardOrLightTariff;

  const toggleCardTitleField = () => {
    setShowInputField(!showInputField);
  };

  const uploadPDFHandler = (e: MouseEvent<HTMLButtonElement, MouseEvent>) => {
    if (isUpdateOnlinePDFAvailable) {
      uploadPDFRef.current?.click();
    } else {
      e.stopPropagation();
    }
  };

  const generatePDFHandler = (e: MouseEvent<HTMLButtonElement, MouseEvent>) => {
    if (isGeneratePDFAvailable) {
      generatePDFRef.current?.click();
    } else {
      e.stopPropagation();
    }
  };

  const { copyLinkHandler, handleFileUpload, inviteAssistantsModal, isPdfGenerated } = useSharePDF({
    uuid,
    //@ts-ignore
    pdfGenerationHandler: uploadPDFHandler,
    track,
    metadata,
    docTitle: title,
    status: rest.status,
  });

  const pdfActionItem = [
    {
      id: CREATE_PDF,
      title: t('createOnlinePDF'),
      icon: <ArrowRightUpIcon />,
      disabled: false,
      clickHandler: uploadPDFHandler,
    },
  ];

  const pdfUpdateItems = [
    {
      id: COPY_PDF_LINK,
      title: t('copyLink'),
      icon: <ChainsIcon />,
      disabled: false,
      clickHandler: copyLinkHandler,
    },
    {
      id: UPDATE_PDF,
      title: t('updateOnlinePDF'),
      icon: <ArrowPathIcon />,
      disabled: false,
      clickHandler: uploadPDFHandler,
    },
  ];
  //@ts-ignore
  const actionsList: ICardActionProperties[] = useMemo(
    () => [
      { id: EDIT, title: t('edit'), icon: <EditIcon />, disabled: false, clickHandler: handlers.EDIT },
      { id: COPY, title: t('copy'), icon: <CopyIcon />, disabled: false, clickHandler: handlers.COPY },
      { id: SHARE, title: t('share'), icon: <ShareIcon />, disabled: false, clickHandler: handlers.SHARE },
      {
        id: RENAME,
        title: t('rename'),
        icon: <RenameIcon />,
        disabled: false,
        clickHandler: () => handlers.RENAME(toggleCardTitleField),
      },
      {
        id: PDF,
        title: t('pdf'),
        icon: <DownloadIcon />,
        disabled: false,
        clickHandler: generatePDFHandler,
      },
      ...(isPdfGenerated ? pdfUpdateItems : pdfActionItem),
      {
        id: ASSISTANT,
        title: t('inviteAssistant'),
        disabled: !isHighestSubscription,
        clickHandler: inviteAssistantsModal,
        icon: <PaperAirplaneIcon />,
      },
      {
        id: DELETE,
        title: t('delete'),
        icon: <TrashBinIcon />,
        disabled: false,
        clickHandler: handlers.DELETE,
      },
    ],
    [isPdfGenerated, t, i18n],
  );

  const currentCardActionsList = useMemo(
    () =>
      getCardActionsList({
        actionsList,
        viewonly,
        availabilityCount,
        userSubscriptionPlan: userSubscription?.currentSubscription.subscriptionPlan,
        type,
      }),
    [actionsList, viewonly, availabilityCount, userSubscription?.currentSubscription.subscriptionPlan, isOnePageBP],
  );

  const stopPropagation = (e: MouseEvent<HTMLElement>) => {
    e.stopPropagation();
  };

  return (
    <>
      <Box className={styles.lowerCard}>
        <Box display={'flex'} justifyContent={'space-between'} gap={'8px'} onClick={stopPropagation}>
          <CardTitleComponent
            showInputField={showInputField}
            title={title}
            toggleCardTitleField={toggleCardTitleField}
            uuid={uuid}
            metadata={metadata}
          />
          <BusinessPlanActionsMenu
            docType={type}
            docTitle={title}
            actionsList={currentCardActionsList}
            uuid={uuid}
            metadata={metadata}
          />
        </Box>
        <Typography className={styles.lastEditedTextStyles} variant="subtitle2">
          {getTimeAgoString(updatedAt, i18n.language)}
        </Typography>
        {viewonly && (
          <Typography variant="subtitle2" className={styles.viewOnlyTextStyles}>
            {t('viewOnly')}
          </Typography>
        )}
      </Box>
      {!isOnePageBP && (
        <>
          <PdfDownloadButton ref={generatePDFRef} className="sr-only" />
          <PdfDownloadButton ref={uploadPDFRef} onFileUpload={handleFileUpload} className="sr-only" />
        </>
      )}
    </>
  );
};
